/**
 * Predictions → Book. The banker's market screen.
 *
 * Rebuilt from `design_handoff_book`. What it replaces was four screens of
 * stacked, separately-scrolling blocks (market selector → price list →
 * predictions list → liabilities summary) restating the same numbers three
 * times, and it never answered the question a banker actually has: what does
 * each person stand to win, and what do I owe under every outcome.
 *
 * Now: a compact market bar, one dark hero carrying the whole position, and
 * ONE list with three lenses. Roughly one screen.
 *
 * THE MATHS IS NOT HERE. Every figure comes from `services/market_book`
 * server-side — the handoff works the model out in full and says to port it
 * as-is, and this app's rule is that the client presents figures rather than
 * deriving them. So a return is computed in one place, and the handoff's two
 * invariants are asserted in tests rather than hoped for in a render.
 *
 * ONE REQUEST. `/tournaments/{id}/book` carries the position AND the result,
 * so the two can never disagree by a refresh.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useApi } from '../useApi';
import { shortName } from './format';
import { useAuth } from '../AuthContext';
import PlaceBetModal from '../PlaceBetModal';
import GenerateOddsModal from '../GenerateOddsModal';
import EditOddsModal from '../EditOddsModal';
import './book.css';

const LENSES = [
  { id: 'runners', label: 'Prices' },
  { id: 'bettors', label: 'Bettors' },
  { id: 'bets', label: 'All bets' },
];

/** `−£130`, `+£65`, `£120`. The minus is U+2212, not a hyphen: at 34/800 a
 *  hyphen reads as a dash against tabular figures. */
const money = (n) => {
  const value = Math.abs(Number(n) || 0);
  const body = value % 1 === 0 ? String(value) : value.toFixed(2);
  return `${Number(n) < 0 ? '−' : ''}£${body}`;
};

const signed = (n) => {
  const value = Math.round((Number(n) || 0) * 100) / 100;
  const mark = value > 0 ? '+' : value < 0 ? '−' : '';
  return `${mark}£${Math.abs(value)}`;
};

/**
 * First names for the chart's column labels, DISAMBIGUATED.
 *
 * The handoff's own field has a Nick Charter and a Nick Blogg. Labelling both
 * columns "Nick" is worse than labelling neither — the reader cannot tell
 * which outcome is which, and the whole point of the labels is that they can.
 * A repeated first name picks up its surname initial; a unique one is left
 * alone, because "Baz B." is noise where "Baz" will do.
 */
const columnLabels = (names) => {
  const firsts = names.map((n) => (n || '').split(/\s+/)[0] || '?');
  const counts = firsts.reduce((acc, f) => {
    acc[f] = (acc[f] || 0) + 1;
    return acc;
  }, {});
  return names.map((name, index) => {
    const first = firsts[index];
    if (counts[first] === 1) return first;
    const rest = (name || '').split(/\s+/).slice(1).filter(Boolean);
    return rest.length ? `${first} ${rest[rest.length - 1][0]}` : first;
  });
};

/** Up, down, or neither — zero is not a gain. */
const tone = (n) => (n > 0 ? ' is-up' : n < 0 ? ' is-down' : '');

const initialsOf = (name) => (name || '?')
  .split(/\s+/).filter(Boolean).slice(0, 2)
  .map((part) => part[0]).join('').toUpperCase() || '?';

const Eyebrow = ({ children, className = '' }) => (
  <span className={`bk-eyebrow ${className}`}>{children}</span>
);

const BookView = () => {
  const { authenticatedFetch } = useApi();
  const { hasPermission } = useAuth();

  const [markets, setMarkets] = useState([]);
  const [marketId, setMarketId] = useState('');
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [lens, setLens] = useState('runners');
  const [sheet, setSheet] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [repricing, setRepricing] = useState(false);
  const [pricingOption, setPricingOption] = useState('standard');
  const [editing, setEditing] = useState(null);      // the runner being repriced
  const [editedOdds, setEditedOdds] = useState('');

  const mayBank = hasPermission ? hasPermission('MANAGE_ODDS') : false;

  // --- the markets a banker can pick from --------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authenticatedFetch('/tournaments?q=&limit=20');
        if (!res.ok || cancelled) return;
        const rows = await res.json();
        if (cancelled) return;
        setMarkets(rows);
        setMarketId((current) => current || (rows[0] ? String(rows[0].id) : ''));
      } catch (e) { /* the empty state covers it */ }
    })();
    return () => { cancelled = true; };
  }, [authenticatedFetch]);

  const load = useCallback(async (id) => {
    if (!id) { setBook(null); return; }
    setLoading(true);
    try {
      const res = await authenticatedFetch(`/tournaments/${id}/book`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setBook(await res.json());
      setError(null);
    } catch (e) {
      // A FAILED REQUEST IS NOT AN ANSWER — blanking would read as "no book".
      setError('Could not load this book.');
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch]);

  useEffect(() => { load(marketId); }, [marketId, load]);

  const market = useMemo(
    () => markets.find((m) => String(m.id) === String(marketId)) || null,
    [markets, marketId]);

  const position = book?.position || null;
  const locked = !!position?.locked;
  const settled = !!book?.settled;

  // ---------------------------------------------------------------- actions
  const toggleLock = async () => {
    if (!mayBank || !position?.priced) return;
    await authenticatedFetch(`/tournaments/${marketId}/lock_odds`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ locked: !locked }),
    });
    load(marketId);
  };

  const reprice = async (option) => {
    const res = await authenticatedFetch(
      `/tournaments/${marketId}/tournament_odds/generate`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pricing: option || pricingOption }),
      },
    );
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      setError(body.error || 'Could not price this field.');
    }
    setRepricing(false);
    load(marketId);
  };

  const placeBet = async (details) => {
    const res = await authenticatedFetch('/bets', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(details),
    });
    if (res.ok) {
      setPlacing(false);
      load(marketId);
    } else {
      const body = await res.json().catch(() => ({}));
      setError(body.error || 'Could not place that prediction.');
    }
  };

  const saveOdds = async () => {
    await authenticatedFetch(`/tournaments/${marketId}/tournament_odds`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        player_odds: [{ player_id: editing.player_id, odds: editedOdds }],
      }),
    });
    setEditing(null);
    load(marketId);
  };

  // ------------------------------------------------------------------ hero
  /**
   * The outcome chart: one column per runner, DIVERGING from a shared zero
   * line — profit grows up, loss grows down. Bottom-aligned bars (which is
   * what the first cut did) put a £1 profit and a £130 loss at the same end
   * of the panel and left the sign to carry the whole difference.
   *
   * TWO DENSITIES. Up to eight runners every column is labelled with its
   * figure and whose it is. Past that the labels stop fitting — a name in a
   * 30px column is three characters — so the bars go thin and unlabelled,
   * only the best and worst stay at full strength, and the caption row names
   * them instead.
   */
  const hero = useMemo(() => {
    if (!position || !position.runners.length) return null;
    const ranked = [...position.runners].sort((a, b) => b.pnl - a.pnl);
    const labels = columnLabels(ranked.map((r) => r.name));
    const span = Math.max(...ranked.map((r) => Math.abs(r.pnl)), 1);
    const flat = ranked.every((r) => r.pnl === 0);
    const bestPnl = ranked[0].pnl;
    const worstPnl = ranked[ranked.length - 1].pnl;

    return {
      dense: ranked.length <= 8,
      flat,
      columns: ranked.map((r, index) => ({
        key: r.player_id,
        name: r.name,
        short: labels[index],
        label: `${r.name} ${signed(r.pnl)}`,
        value: signed(r.pnl),
        up: r.pnl > 0,
        down: r.pnl < 0,
        // A zero outcome draws nothing; anything else keeps a visible stub so
        // a small position is not rounded out of existence.
        height: r.pnl === 0 ? 0
          : Math.max(3, Math.round(Math.abs(r.pnl) / span * 30)),
        // In sparse mode only the two extremes are at full strength.
        muted: ranked.length > 8 && r.pnl !== bestPnl && r.pnl !== worstPnl,
      })),
      best: ranked[0],
      worst: ranked[ranked.length - 1],
    };
  }, [position]);

  const maxPayout = useMemo(
    () => Math.max(...(position?.runners || []).map((r) => r.payout), 1),
    [position]);

  // ----------------------------------------------------------------- render
  if (!loading && !markets.length) {
    return (
      <div className="bk">
        <div className="bk-empty">
          No competitions yet — a book needs something to price.
        </div>
      </div>
    );
  }

  return (
    <div className="bk">
      {error ? <div className="bk-error">{error}</div> : null}

      {/* 1. MARKET BAR ------------------------------------------------- */}
      <div className="bk-marketbar">
        <button type="button" className="bk-marketbar-main"
                onClick={() => setSheet(true)}>
          <span className="bk-marketbar-text">
            <Eyebrow>MARKET</Eyebrow>
            <span className="bk-marketbar-name">
              {market ? market.name : 'Pick a market'}
            </span>
          </span>
          <span className="bk-caret" aria-hidden="true">▾</span>
        </button>
        <button
          type="button"
          className={`bk-status${locked ? ' is-locked' : ''}`}
          onClick={toggleLock}
          disabled={!mayBank || !position?.priced}
        >
          {locked ? 'LOCKED' : 'OPEN'}
        </button>
      </div>

      {/* 2. HERO -------------------------------------------------------- */}
      {position && position.priced ? (
        <div className="bk-hero">
          {settled ? (
            /* THE BOOK HAS FINISHED. The handoff has no settled state — it
               designs the open market — but the result must not vanish when
               the golf ends, so it takes the hero's place. Same panel, same
               weight: it is still the answer to "where does the book stand". */
            <>
              <div className="bk-hero-top">
                <div className="bk-hero-main">
                  <Eyebrow className="bk-hero-eyebrow">
                    {book.joint ? 'JOINT WINNERS' : 'WINNER'}
                  </Eyebrow>
                  <div className="bk-hero-figure bk-hero-won">
                    {book.winners.map((w) => w.name).join(' & ')}
                  </div>
                  <div className="bk-hero-caption">
                    {money(book.staked)} in · {money(book.returned)} returned
                    {book.joint ? ' · joint winners return in full' : ''}
                  </div>
                </div>
                <div className="bk-hero-side">
                  <div className="bk-hero-stat">
                    <Eyebrow className="bk-hero-eyebrow">BOOK KEPT</Eyebrow>
                    <span className={`bk-hero-value${
                      tone(book.staked - book.returned)}`}>
                      {signed(book.staked - book.returned)}
                    </span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="bk-hero-top">
                <div className="bk-hero-main">
                  <Eyebrow className="bk-hero-eyebrow">BOOK POSITION</Eyebrow>
                  <div className="bk-hero-headline">
                    <span className={`bk-hero-figure${
                      tone(position.worst.pnl)}`}>
                      {signed(position.worst.pnl)}
                    </span>
                    <span className="bk-hero-qualifier">worst case</span>
                  </div>
                  <div className="bk-hero-caption">
                    {/* A PRICED MARKET WITH NO BETS is flat, not "in front" —
                        there is nothing staked to be in front of. */}
                    {position.staked === 0
                      ? 'Prices are up. Nothing staked yet.'
                      : position.worst.pnl < 0
                        ? `If ${position.worst.name} wins you pay out ${
                          money(position.worst.payout)} against ${
                          money(position.staked)} in`
                        : 'Every result leaves the book in front'}
                  </div>
                </div>
                <div className="bk-hero-side">
                  <div className="bk-hero-stat">
                    <Eyebrow className="bk-hero-eyebrow">BEST CASE</Eyebrow>
                    <span className={`bk-hero-value${
                      tone(position.best.pnl)}`}>
                      {signed(position.best.pnl)}
                    </span>
                  </div>
                  <div className="bk-hero-stat">
                    <Eyebrow className="bk-hero-eyebrow">PROJECTED</Eyebrow>
                    <span className={`bk-hero-value${
                      tone(position.projected ?? 0)}`}>
                      {signed(position.projected ?? 0)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Colour is never the only cue: every column carries its
                  runner and figure as text (on the column when there is room,
                  in the title attribute always), and the caption says the
                  same thing in words. */}
              <div
                className={`bk-chart${hero.dense ? '' : ' bk-chart--sparse'}`}
                role="img"
                aria-label={hero.flat
                  ? 'Every outcome leaves the book level'
                  : `Outcomes from best, ${hero.best.name} ${
                    signed(hero.best.pnl)}, to worst, ${hero.worst.name} ${
                    signed(hero.worst.pnl)}`}
              >
                {hero.columns.map((column) => (
                  <span key={column.key} className="bk-col"
                        title={column.label}>
                    <span className="bk-col-cap">
                      {hero.dense && column.up ? column.value : ''}
                    </span>
                    <span className="bk-col-up">
                      {column.up ? (
                        <span className={`bk-col-bar is-up${
                          column.muted ? ' is-muted' : ''}`}
                              style={{ height: `${column.height}px` }} />
                      ) : null}
                    </span>
                    {/* The shared zero line. Every column draws it, so it
                        reads as one rule across the panel. */}
                    <span className="bk-col-zero" />
                    <span className="bk-col-down">
                      {column.down ? (
                        <span className={`bk-col-bar is-down${
                          column.muted ? ' is-muted' : ''}`}
                              style={{ height: `${column.height}px` }} />
                      ) : null}
                    </span>
                    <span className="bk-col-cap">
                      {hero.dense && column.down ? column.value : ''}
                    </span>
                    {hero.dense ? (
                      <span className="bk-col-name">{column.short}</span>
                    ) : null}
                  </span>
                ))}
              </div>
              <div className="bk-strip-caption">
                {hero.flat ? (
                  <span>No predictions yet — every outcome is level</span>
                ) : (
                  <>
                    {/* Sparse mode drops the column labels, so the names move
                        here rather than disappearing. */}
                    <span>
                      Best {hero.dense ? '' : `${shortName(hero.best.name)} `}
                      {signed(hero.best.pnl)}
                    </span>
                    <span>
                      {money(position.staked)} staked · Worst{' '}
                      {hero.dense ? '' : `${shortName(hero.worst.name)} `}
                      {signed(hero.worst.pnl)}
                    </span>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      ) : null}

      {!position?.priced && !loading ? (
        <div className="bk-empty">
          No prices yet.{mayBank
            ? ' Tap Generate to open a book on this competition.'
            : ' The organiser has not opened a book here.'}
        </div>
      ) : null}

      {/* 3. LENS SWITCH -------------------------------------------------- */}
      {position?.priced ? (
        <div className="bk-lenses">
          {LENSES.map((option) => (
            <button
              key={option.id}
              type="button"
              className={`bk-lens${lens === option.id ? ' is-selected' : ''}`}
              onClick={() => setLens(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
      ) : null}

      {/* 4a. PRICES ------------------------------------------------------ */}
      {position?.priced && lens === 'runners' ? (
        <div className="bk-card">
          <div className="bk-card-head">
            <Eyebrow>BOOK PRICES</Eyebrow>
            <span className="bk-overround">
              Overround {position.overround}%
            </span>
          </div>
          <div className="bk-prices-head">
            <span className="bk-col-price">PRICE</span>
            <span className="bk-col-runner">RUNNER</span>
            <span className="bk-col-backed">BACKED</span>
            <span className="bk-col-pnl">IF WINS</span>
          </div>
          {position.runners.map((runner) => (
            <div key={runner.player_id} className="bk-price-row">
              <span className="bk-col-price bk-price-cell">
                <button type="button" className="bk-odds"
                        disabled={!mayBank || locked}
                        onClick={() => {
                          setEditing(runner);
                          setEditedOdds(runner.odds);
                        }}>
                  {runner.odds}
                </button>
                <span className="bk-implied">{runner.implied}%</span>
              </span>
              <span className="bk-col-runner bk-runner-cell">
                <span className="bk-runner-name" title={runner.name}>
                  {runner.name}
                </span>
                <span className="bk-liability">
                  <span
                    className={`bk-liability-fill${
                      runner.pnl < 0 ? ' is-down' : ''}`}
                    style={{
                      width: `${runner.payout
                        ? Math.max(runner.payout / maxPayout * 100, 4) : 0}%`,
                    }}
                  />
                </span>
              </span>
              <span className="bk-col-backed bk-backed">
                {runner.backed ? money(runner.backed) : '—'}
              </span>
              <span className={`bk-col-pnl bk-pnl${
                runner.pnl > 0 ? ' is-up' : runner.pnl < 0 ? ' is-down' : ''}`}>
                {signed(runner.pnl)}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      {/* 4b. BETTORS ----------------------------------------------------- */}
      {position?.priced && lens === 'bettors' ? (
        <div className="bk-bettors">
          {position.bettors.map((bettor) => (
            <div key={bettor.name} className="bk-card bk-bettor">
              <div className="bk-bettor-head">
                <span className="bk-avatar">{initialsOf(bettor.name)}</span>
                <span className="bk-bettor-id">
                  <span className="bk-bettor-name">{bettor.name}</span>
                  <span className="bk-bettor-meta">
                    {bettor.bets.length}{' '}
                    {bettor.bets.length === 1 ? 'prediction' : 'predictions'}
                    {' · '}{money(bettor.staked)} staked
                  </span>
                </span>
                <span className="bk-bettor-max">
                  <Eyebrow>MAX WIN</Eyebrow>
                  <span className="bk-bettor-max-value">
                    {signed(bettor.max_win)}
                  </span>
                </span>
              </div>
              <div className="bk-tiles">
                <span className="bk-tile">
                  <Eyebrow className="bk-tile-label">STAKED</Eyebrow>
                  <span className="bk-tile-value">{money(bettor.staked)}</span>
                </span>
                <span className="bk-tile">
                  <Eyebrow className="bk-tile-label">AT RISK</Eyebrow>
                  <span className="bk-tile-value is-down">
                    {money(bettor.at_risk)}
                  </span>
                </span>
                <span className="bk-tile">
                  <Eyebrow className="bk-tile-label">BEST RUNNER</Eyebrow>
                  {/* A third-width tile cannot hold a full name — measured at
                      147px against 78. The bettor's own lines below carry the
                      full name, so the tile takes the first. */}
                  <span className="bk-tile-value bk-tile-name"
                        title={bettor.best_runner}>
                    {shortName(bettor.best_runner)}
                  </span>
                </span>
              </div>
              <div className="bk-bettor-lines">
                {bettor.bets.map((bet) => (
                  <span key={bet.id} className="bk-bettor-line">
                    <span className="bk-bettor-line-text">
                      {money(bet.stake)} on {bet.runner} at {bet.odds}
                    </span>
                    <span className="bk-bettor-line-return">
                      {money(bet.returns)}
                    </span>
                  </span>
                ))}
              </div>
            </div>
          ))}
          {!position.bettors.length ? (
            <div className="bk-empty">No predictions on this book yet.</div>
          ) : null}
        </div>
      ) : null}

      {/* 4c. ALL BETS ---------------------------------------------------- */}
      {position?.priced && lens === 'bets' ? (
        <div className="bk-card bk-card--list">
          {position.bets.map((bet) => (
            <div key={bet.id} className="bk-bet-row">
              <span className="bk-avatar bk-avatar--quiet">
                {initialsOf(bet.name)}
              </span>
              <span className="bk-bet-id">
                <span className="bk-bet-runner">{bet.runner}</span>
                <span className="bk-bet-meta">
                  {bet.name} · {money(bet.stake)} at {bet.odds}
                </span>
              </span>
              <span className="bk-bet-return">
                <span className="bk-bet-return-value">
                  {money(bet.returns)}
                </span>
                <span className="bk-bet-return-label">returns</span>
              </span>
            </div>
          ))}
          {!position.bets.length ? (
            <div className="bk-empty">No predictions on this book yet.</div>
          ) : null}
        </div>
      ) : null}

      {/* ACTION FOOTER --------------------------------------------------- */}
      {mayBank || position?.priced ? (
        <div className="bk-footer">
          {/* GENERATE or REGENERATE, because they are different acts. The
              first opens a book on a field that has none; the second moves
              prices, possibly ones bets were already struck at. And with
              nothing priced yet this is the only live control on the screen,
              so it takes the primary weight until it is done. */}
          {mayBank ? (
            <button
              type="button"
              className={`bk-reprice${
                position?.priced ? '' : ' bk-reprice--primary'}`}
              disabled={locked || settled}
              onClick={() => setRepricing(true)}
            >
              {position?.priced ? 'Regenerate' : 'Generate'}
            </button>
          ) : null}
          {position?.priced ? (
            <button
              type="button"
              className="bk-cta"
              disabled={locked || settled}
              onClick={() => setPlacing(true)}
            >
              {settled ? 'Result is in'
                : locked ? 'Market locked'
                  : '+  Place a prediction'}
            </button>
          ) : null}
        </div>
      ) : null}

      {/* MARKET SHEET ---------------------------------------------------- */}
      {sheet ? (
        <div className="bk-sheet-wrap">
          <button type="button" className="bk-scrim" aria-label="Close"
                  onClick={() => setSheet(false)} />
          <div className="bk-sheet" role="dialog" aria-label="Pick a market">
            <div className="bk-sheet-head">
              <span className="bk-sheet-title">Pick a market</span>
              <button type="button" className="bk-sheet-close"
                      onClick={() => setSheet(false)}>Close</button>
            </div>
            <div className="bk-sheet-list">
              {markets.map((option) => {
                const on = String(option.id) === String(marketId);
                return (
                  <button
                    key={option.id}
                    type="button"
                    className={`bk-sheet-row${on ? ' is-selected' : ''}`}
                    onClick={() => { setMarketId(String(option.id));
                                     setSheet(false); }}
                  >
                    <span className="bk-sheet-row-main">
                      <span className="bk-sheet-row-name">{option.name}</span>
                      <span className="bk-sheet-row-sub">
                        Outright winner
                        {option.format ? ` · ${option.format}` : ''}
                        {option.date ? ` · ${option.date}` : ''}
                      </span>
                    </span>
                    {on ? <span className="bk-sheet-tick">Selected</span> : null}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}

      {placing && position ? (
        <PlaceBetModal
          selectedTournament={marketId}
          odds={position.runners.map((r) => ({
            player_id: r.player_id, player_name: r.name, odds: r.odds,
          }))}
          onPlaceBet={placeBet}
          onCancel={() => setPlacing(false)}
        />
      ) : null}

      {/* These two are the existing modals. They take `isOpen`/`onConfirm`
          and read `player_name` — passing the wrong prop names makes them
          render nothing at all rather than fail loudly. */}
      <GenerateOddsModal
        isOpen={repricing}
        pricingOption={pricingOption}
        setPricingOption={setPricingOption}
        onConfirm={() => reprice(pricingOption)}
        onClose={() => setRepricing(false)}
      />

      {editing ? (
        <EditOddsModal
          player={{ ...editing, player_name: editing.name }}
          currentOdds={editedOdds}
          onOddsChange={setEditedOdds}
          onSave={saveOdds}
          onCancel={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
};

export default BookView;
